import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import { selectContentLibraryOpenedFile } from 'features/content-library/contentLibrarySlice';
import { GridPanelControl } from 'features/layout/grid-panel/GridPanelControl';
import { useSystemSession } from 'hooks/useSystemSession';
import { isPrivateWhiteboard } from 'features/content-library/utils/isPrivateWhiteboard';
import { useWhiteboardControl } from 'features/toolbar/toolbar/controls/whiteboard/useWhiteboardControl';
import { selectGridPanel } from 'features/layout/features/content/contentSlice';

export const useWhiteboardModeToggleControl = () => {
  const isSystemSession = useSystemSession();

  const { t } = useTranslation('room');

  const { isFeatureAvailable, canManagePrivateWhiteboards, toggleWhiteboardMode } =
    useWhiteboardControl();

  const currentGridPanel = useAppSelector(selectGridPanel);
  const openedFile = useAppSelector(selectContentLibraryOpenedFile);
  const isWhiteboardOpened = openedFile?.type === 'whiteboard';
  const isPrivate = isPrivateWhiteboard(openedFile);

  const controlEnabled =
    !isSystemSession &&
    isFeatureAvailable &&
    canManagePrivateWhiteboards &&
    isWhiteboardOpened &&
    currentGridPanel === 'whiteboard';

  const handleClick = () => {
    if (openedFile) {
      toggleWhiteboardMode(openedFile.id, isPrivate);
    }
  };

  return {
    isPrivate,
    controlEnabled,
    handleClick,
    tooltip: isPrivate
      ? t('tooltips.make_whiteboard_public')
      : t('tooltips.make_whiteboard_private'),
    icon: isPrivate ? 'eye-crossed' : 'eye',
  };
};

export const WhiteboardModeToggleControl = () => {
  const { t } = useTranslation('room');

  const {
    isPrivate,
    handleClick: handleToggleMode,
    controlEnabled,
    tooltip,
  } = useWhiteboardModeToggleControl();

  if (!controlEnabled) {
    return null;
  }

  return (
    <GridPanelControl
      icon={isPrivate ? 'eye-crossed' : 'eye'}
      tooltip={tooltip}
      onClick={handleToggleMode}
      text={isPrivate ? t('grid.private_mode') : t('grid.public_mode')}
      sx={[
        {
          px: 3,
          width: 'auto',
          borderRadius: '20px',
          '&:after': {
            borderRadius: '12px',
          },
        },
        isPrivate
          ? {
              color: 'white',
              backgroundColor: 'primary.main',
              borderColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.main',
              },
            }
          : {},
      ]}
    />
  );
};
